<template>
  <div class="flex flex-col">
    <h3 class="text-display-small mb-4">{{ feedbackTopic.title }}</h3>
    <div class="mb-2 flex w-full justify-end pr-1">
      <PerspectiveLegend :all-perspectives="topicPerspectives" />
    </div>
    <div class="grid w-full grid-cols-1 items-start gap-4 md:grid-cols-12">
      <div class="col-span-1 flex shrink-0 grow-0 flex-col gap-0.5 md:col-span-6 2xl:col-span-4">
        <div class="bg-surface-container rounded-large p-4 md:p-8">
          <MenonChart
            :skill-analysis="allSkills"
            :max-value="scale.items.length"
            :topic-perspectives="topicPerspectives"
          />
        </div>
      </div>
      <div class="col-span-1 min-w-0 grow md:col-span-6 2xl:col-span-8">
        <div class="@container">
          <div class="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
            <FinalQuestionSection
              :questions="strengths"
              :topic-perspectives="topicPerspectives"
              :scale="scaleItems"
              :title="$t('dialog.evaluation.slide.strengths')"
            />
            <FinalQuestionSection
              :questions="potentials"
              :topic-perspectives="topicPerspectives"
              :scale="scaleItems"
              :title="$t('dialog.evaluation.slide.potentials')"
            />
            <FinalQuestionSection
              :questions="overestimates"
              :topic-perspectives="topicPerspectives"
              :scale="scaleItems"
              :title="$t('dialog.evaluation.slide.overestimates')"
            />
            <FinalQuestionSection
              :questions="underestimates"
              :topic-perspectives="topicPerspectives"
              :scale="scaleItems"
              :title="$t('dialog.evaluation.slide.underestimates')"
            />
          </div>
        </div>
        <FinalTextFeedbacks class="mt-4" :texts="texts" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import type { FeedbackTopic } from '../../../core/models/feedback-topic'
  import type { Scale } from '../../../core/models/scale'
  import MenonChart from '../../../core/components/analytics/menon-chart/MenonChart.vue'
  import type { Skill } from '../../../core/models/skill'
  import FinalQuestionSection from './FinalQuestionSection.vue'
  import type {
    QuestionAnalysis,
    SkillAnalysis,
    TextAnalysis,
  } from '@/core/models/surveys-analytics'
  import { useSkillAnalysis } from '@/core/composables/use-skill-analysis'
  import FinalTextFeedbacks from './FinalTextFeedbacks.vue'
  import { computed } from 'vue'
  import { perspectivesFromTopic } from '@/core/models/perspective'
  import PerspectiveLegend from '@/core/components/analytics/question-list/PerspectiveLegend.vue'
  interface Props {
    feedbackTopic: FeedbackTopic & { skills: ReadonlyArray<Skill> }
    skillData: ReadonlyArray<SkillAnalysis>
    strengths: ReadonlyArray<QuestionAnalysis>
    potentials: ReadonlyArray<QuestionAnalysis>
    overestimates: ReadonlyArray<QuestionAnalysis>
    underestimates: ReadonlyArray<QuestionAnalysis>
    texts: ReadonlyArray<TextAnalysis>
    scale: Scale
  }

  const props = defineProps<Props>()
  const { allSkills } = useSkillAnalysis(props.skillData)
  const topicPerspectives = computed(() => perspectivesFromTopic(props.feedbackTopic))
  const scaleItems = computed(() =>
    props.scale.items.map((item) => ({ value: item.value, label: item.label ?? '' })),
  )
</script>
